<template>
  <!-- Modal de Receita BPO (Não-Fixa) -->
  <CModal
    v-model:visible="modalReceitaBpo.exibir"
    size="xl"
    alignment="center"
    @close="fecharModalReceitaBpo"
  >
    <CModalHeader>
      <CModalTitle>
        <font-awesome-icon icon="fas fa-dollar-sign" class="me-2" />
        Detalhes da Receita
      </CModalTitle>
    </CModalHeader>
    <CModalBody v-if="modalReceitaBpo.receita">
      <!-- Alerta de cartório diferente -->
      <CAlert
        v-if="modalReceitaBpo.cartorioDiferente && modalReceitaBpo.cartorioInfo"
        color="info"
        class="mb-3"
      >
        <div class="d-flex align-items-center justify-content-between">
          <div>
            <CIcon icon="cil-info" class="me-2" />
            Esta receita pertence ao cartório {{ usuarioStore.formatarNomeCartorio(modalReceitaBpo.cartorioInfo) }}, diferente do cartório atualmente selecionado.
          </div>
          <CButton
            color="primary"
            size="sm"
            @click="trocarCartorio"
          >
            Trocar para este cartório
          </CButton>
        </div>
      </CAlert>

      <CRow>
        <!-- Informações Básicas -->
        <CCol md="12" class="mb-4">
          <h5 class="mb-3 text-primary">
            <font-awesome-icon icon="fas fa-info-circle" class="me-2" />
            Informações Gerais
          </h5>
          <CRow>
            <CCol md="8" class="mb-3">
              <h6 class="text-muted mb-1">
                Descrição
              </h6>
              <p class="mb-0 fs-5">
                {{ modalReceitaBpo.receita.descricao || 'Sem descrição' }}
              </p>
            </CCol>
            <CCol md="4" class="mb-3">
              <h6 class="text-muted mb-1">
                Tipo
              </h6>
              <CBadge :color="modalReceitaBpo.receita.tipo === 'UNICA' ? 'success' : 'primary'">
                {{ modalReceitaBpo.receita.tipo }}
              </CBadge>
            </CCol>
            <CCol md="6" class="mb-3">
              <h6 class="text-muted mb-1">
                Valor Total
              </h6>
              <p class="mb-0 text-success">
                R$ {{ parseFloat(modalReceitaBpo.receita.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
              </p>
            </CCol>
            <CCol md="6" class="mb-3">
              <h6 class="text-muted mb-1">
                Data
              </h6>
              <p class="mb-0">
                {{ modalReceitaBpo.receita.data ? new Date(modalReceitaBpo.receita.data).toLocaleDateString('pt-BR') : '-' }}
              </p>
            </CCol>
          </CRow>
        </CCol>

        <!-- Pagador e Favorecido -->
        <CCol md="12" class="mb-4">
          <h5 class="mb-3 text-primary">
            <font-awesome-icon icon="fas fa-users" class="me-2" />
            Partes Envolvidas
          </h5>
          <CRow v-if="pagadorEFavorecidoSaoIguais(modalReceitaBpo.receita)">
            <CCol md="12" class="mb-3">
              <CCard>
                <CCardBody>
                  <h6 class="card-title text-muted mb-2">
                    <font-awesome-icon icon="fas fa-user" class="me-2" />
                    Pagador/Favorecido
                  </h6>
                  <p v-if="modalReceitaBpo.receita.pagador" class="mb-1 fw-bold">
                    {{ modalReceitaBpo.receita.pagador.razao_nome }}
                  </p>
                  <p v-if="modalReceitaBpo.receita.pagador" class="mb-2 text-muted">
                    CPF/CNPJ: {{ formatarCpfCnpj(modalReceitaBpo.receita.pagador.cpf_cnpj) }}
                  </p>
                  <CBadge color="info" class="me-2">
                    <font-awesome-icon icon="fas fa-info-circle" class="me-1" />
                    Pagador e favorecido são a mesma pessoa
                  </CBadge>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
          <CRow v-else>
            <CCol md="6" class="mb-3">
              <CCard>
                <CCardBody>
                  <h6 class="card-title text-muted mb-2">
                    <font-awesome-icon icon="fas fa-hand-holding-usd" class="me-2" />
                    Pagador
                  </h6>
                  <p v-if="modalReceitaBpo.receita.pagador" class="mb-1 fw-bold">
                    {{ modalReceitaBpo.receita.pagador.razao_nome }}
                  </p>
                  <p v-if="modalReceitaBpo.receita.pagador" class="mb-0 text-muted">
                    CPF/CNPJ: {{ formatarCpfCnpj(modalReceitaBpo.receita.pagador.cpf_cnpj) }}
                  </p>
                  <p v-else class="mb-0 text-muted">
                    Não informado
                  </p>
                </CCardBody>
              </CCard>
            </CCol>
            <CCol md="6" class="mb-3">
              <CCard>
                <CCardBody>
                  <h6 class="card-title text-muted mb-2">
                    <font-awesome-icon icon="fas fa-user-check" class="me-2" />
                    Favorecido
                  </h6>
                  <p v-if="modalReceitaBpo.receita.favorecido" class="mb-1 fw-bold">
                    {{ modalReceitaBpo.receita.favorecido.razao_nome }}
                  </p>
                  <p v-if="modalReceitaBpo.receita.favorecido" class="mb-0 text-muted">
                    CPF/CNPJ: {{ formatarCpfCnpj(modalReceitaBpo.receita.favorecido.cpf_cnpj) }}
                  </p>
                  <p v-else class="mb-0 text-muted">
                    Não informado
                  </p>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        </CCol>

        <!-- Serviços -->
        <CCol v-if="modalReceitaBpo.receita.servicos && modalReceitaBpo.receita.servicos.length > 0" md="12" class="mb-4">
          <h5 class="mb-3 text-primary">
            <font-awesome-icon icon="fas fa-clipboard-list" class="me-2" />
            Serviços
          </h5>
          <CTable striped hover>
            <CTableHead>
              <CTableRow>
                <CTableHeaderCell>Serviço</CTableHeaderCell>
                <CTableHeaderCell class="text-center">
                  Quantidade
                </CTableHeaderCell>
                <CTableHeaderCell class="text-end">
                  Valor Unitário
                </CTableHeaderCell>
                <CTableHeaderCell class="text-end">
                  Valor Total
                </CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              <CTableRow v-for="servico in modalReceitaBpo.receita.servicos" :key="servico.id">
                <CTableDataCell>
                  {{ servico.servico ? servico.servico.nome : 'Serviço não especificado' }}
                </CTableDataCell>
                <CTableDataCell class="text-center">
                  {{ servico.quantidade || 1 }}
                </CTableDataCell>
                <CTableDataCell class="text-end">
                  R$ {{ calcularValorUnitario(servico).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                </CTableDataCell>
                <CTableDataCell class="text-end">
                  R$ {{ parseFloat(servico.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                </CTableDataCell>
              </CTableRow>
            </CTableBody>
          </CTable>
        </CCol>

        <!-- Forma de Pagamento -->
        <CCol v-if="modalReceitaBpo.receita.pagamentos && modalReceitaBpo.receita.pagamentos.length > 0" md="12" class="mb-3">
          <h5 class="mb-3 text-primary">
            <font-awesome-icon icon="fas fa-credit-card" class="me-2" />
            Formas de Pagamento
          </h5>
          <CListGroup>
            <CListGroupItem v-for="pagamento in modalReceitaBpo.receita.pagamentos" :key="pagamento.id">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <div class="d-flex align-items-center mb-1">
                    <font-awesome-icon :icon="getIconePagamento(pagamento.forma_de_pagamento)" class="me-2" />
                    <strong>{{ getLabelPagamento(pagamento.forma_de_pagamento) }}</strong>
                    <CBadge v-if="pagamento.parcela > 1" color="secondary" class="ms-2">
                      Parcela {{ pagamento.parcela }}
                    </CBadge>
                  </div>
                  <small v-if="pagamento.data_pagamento" class="text-muted">
                    <font-awesome-icon icon="fas fa-calendar" class="me-1" />
                    {{ new Date(pagamento.data_pagamento).toLocaleDateString('pt-BR') }}
                  </small>
                </div>
                <CBadge color="success" class="fs-6">
                  R$ {{ parseFloat(pagamento.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                </CBadge>
              </div>
            </CListGroupItem>
          </CListGroup>
        </CCol>
      </CRow>
    </CModalBody>
    <CModalFooter>
      <CButton color="secondary" @click="fecharModalReceitaBpo">
        Fechar
      </CButton>
    </CModalFooter>
  </CModal>
</template>

<script setup lang="ts">
const store = usePrestacaoDeContaStore()
const usuarioStore = useUsuarioStore()
const { modalReceitaBpo } = storeToRefs(store)

const fecharModalReceitaBpo = () => {
  store.resetarModalReceitaBpo()
}

// Função para trocar cartório
async function trocarCartorio () {
  try {
    if (modalReceitaBpo.value.cartorioInfo?.id) {
      await usuarioStore.selecionarCartorio(modalReceitaBpo.value.cartorioInfo.id, true)
      // Modal permanece aberto com manterContexto = true

      // Atualizar flags para ocultar o alerta
      modalReceitaBpo.value.cartorioDiferente = false
      modalReceitaBpo.value.cartorioInfo = null
    }
  } catch (error) {
    console.error('Erro ao trocar cartório:', error)
    useStore().toasts.push({
      content: 'Erro ao trocar de cartório',
      color: 'danger',
    })
  }
}

// Função para formatar CPF/CNPJ
const formatarCpfCnpj = (valor: string) => {
  if (!valor) { return '-' }

  // Remove caracteres não numéricos
  const numeros = valor.replace(/\D/g, '')

  // CPF
  if (numeros.length === 11) {
    return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  }

  // CNPJ
  if (numeros.length === 14) {
    return numeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  }

  return valor
}

// Função para obter ícone do tipo de pagamento
const getIconePagamento = (tipo: string) => {
  const icones: Record<string, string> = {
    DINHEIRO: 'fas fa-money-bill-wave',
    PIX: 'fas fa-qrcode',
    CARTAO_CREDITO: 'fas fa-credit-card',
    CARTAO_DEBITO: 'fas fa-credit-card',
    TRANSFERENCIA: 'fas fa-exchange-alt',
    BOLETO: 'fas fa-barcode',
    CHEQUE: 'fas fa-money-check',
  }
  return icones[tipo] || 'fas fa-dollar-sign'
}

// Função para obter label do tipo de pagamento
const getLabelPagamento = (tipo: string) => {
  const labels: Record<string, string> = {
    DINHEIRO: 'Dinheiro',
    PIX: 'PIX',
    CARTAO_CREDITO: 'Cartão de Crédito',
    CARTAO_DEBITO: 'Cartão de Débito',
    TRANSFERENCIA: 'Transferência Bancária',
    BOLETO: 'Boleto',
    CHEQUE: 'Cheque',
  }
  return labels[tipo] || tipo
}

// Função para calcular valor unitário
const calcularValorUnitario = (servico: any) => {
  const valor = parseFloat(servico.valor || 0)
  const quantidade = parseInt(servico.quantidade || 1)
  return quantidade > 0 ? valor / quantidade : 0
}

// Função para verificar se pagador e favorecido são a mesma pessoa
const pagadorEFavorecidoSaoIguais = (receita: any) => {
  if (!receita?.pagador || !receita?.favorecido) {
    return false
  }
  return receita.pagador.id === receita.favorecido.id
}
</script>
